import {
  ArgumentError,
  UnauthorizedError,
  AuthTokenNotSetError,
} from './errors';

export function requireArgs(...required) {
  return function decorator(next) {
    return function decorated(payload = {}) {
      const missing = required.reduce((acc, key) => {
        if (typeof payload[key] === 'undefined') {
          acc.push(key);
        }
        return acc;
      }, []);

      if (missing.length) {
        const error = new ArgumentError(`required arguments: ${missing.join()}`);
        return Promise.reject(error);
      }

      return next.call(this, payload);
    };
  };
}

export function isZeroOrPositiveInteger(n) {
  // Is it a number?
  return typeof n === 'number'
    // Is it an integer?
    && Math.floor(n) === n
    // Is it zero or positive?
    && n >= 0;
}

export const getType = elem => Object.prototype.toString.call(elem).slice(8, -1);
export const isObject = elem => getType(elem) === 'Object';
export const isArray = elem => getType(elem) === 'Array';
export const isString = elem => getType(elem) === 'String';
export const isNumber = elem => getType(elem) === 'Number';
export const isBoolean = elem => getType(elem) === 'Boolean';
export const isFunction = elem => ['Function', 'AsyncFunction'].includes(getType(elem));

export function assertAuthData(authData) {
  if (!authData) {
    throw new AuthTokenNotSetError();
  }
}
export function assertRequiredString(value, propName) {
  if (!isString(value)) {
    throw new ArgumentError(`${propName} is required and must be a string`);
  }
}

export function assertString(value, propName) {
  if (value !== undefined && !isString(value)) {
    throw new ArgumentError(`${propName} must be a string`);
  }
}

export function assertBoolean(value, propName) {
  if (value !== undefined && !isBoolean(value)) {
    throw new ArgumentError(`${propName} must be a boolean`);
  }
}

export function assertStringArray(value, propName) {
  if (value === undefined) return;
  if (!(isArray(value) && value.every(isString))) {
    throw new ArgumentError(`${propName} must be an array of strings`);
  }
}

export function assertRequiredStringArray(value, propName) {
  if (!(isArray(value) && value.every(isString))) {
    throw new ArgumentError(`${propName} is required and must be an array of strings`);
  }
}

export function assertNumber(value, propName) {
  if (value !== undefined && !isNumber(value)) {
    throw new ArgumentError(`${propName} must be a number`);
  }
}

export function assertRequiredNumberArray(value, propName) {
  if (!(isArray(value) && value.every(isNumber))) {
    throw new ArgumentError(`${propName} is required and must be an array of number`);
  }
}

export function assertFunction(value, propName) {
  if (value !== undefined && !isFunction(value)) {
    throw new ArgumentError(`${propName} must be a function`);
  }
}

export function assertRequiredFunction(value, propName) {
  if (!isFunction(value)) {
    throw new ArgumentError(`${propName} is required and must be a function`);
  }
}

export function assertObject(value, propName) {
  if (value !== undefined && !isObject(value)) {
    throw new ArgumentError(`${propName} must be an object`);
  }
}

export function assertRequiredObject(value, propName) {
  if (!isObject(value)) {
    throw new ArgumentError(`${propName} is required and must be an object`);
  }
}

export function assertRequiredNonEmptyObject(value, propName) {
  if (!isObject(value) || !Object.keys(value).length) {
    throw new ArgumentError(`${propName} is required and must be a non-empty object`);
  }
}

export function assertAuthorized(res) {
  if (res.status === 401) {
    throw new UnauthorizedError(res.body.message);
  }
}
